import { useRuntimeConfig, useRequestEvent, useNuxtApp } from "#app";
import { repos } from "@chatfood/dbe-client";
import type { Fetcher } from "@chatfood/fetcher";
import { fetcher } from "@chatfood/fetcher";
import { appendResponseHeader, getCookie } from "h3";
import { parseSetCookie, stringifySetCookie } from "cookie";

const DBE_SESSION_COOKIE_NAME = "dbe.session_id";

export const useDirectBackend = () => {
  const config = useRuntimeConfig();
  const event = useRequestEvent();

  const getSelfDomain = () => {
    if (import.meta.client) {
      return window.location.origin;
    }

    // During SSR, use the actual request URL to ensure consistency
    if (event?.node?.req?.headers?.host) {
      // TODO: point of DX improvement
      // Assuming we use HTTPs only - to avoid issues in prod or development with staging
      return `https://${event.node.req.headers.host}`;
    }

    // Fallback to config if request info not available
    return config.ssrSelfDomain;
  };

  const apiUrl = `${getSelfDomain()}/api/dbe-proxy`;

  const service: Fetcher = fetcher({
    apiUrl,
    acceptLanguage: useNuxtApp().$i18n.locale.value,
    credentials: "include",
    retry: 1,
    timeout: false,
    onRequest: (request) => {
      if (!event) {
        return;
      }

      const cookieFromHeader = getCookie(event, DBE_SESSION_COOKIE_NAME);
      const cookieFromContext = event.context.dbeSessionId;

      const sessionToUse = cookieFromContext ?? cookieFromHeader;

      if (sessionToUse) {
        request.headers.set(
          "cookie",
          `${DBE_SESSION_COOKIE_NAME}=${sessionToUse}`,
        );
      }
    },
    onResponse: (_request, response) => {
      if (!event) {
        return response;
      }

      try {
        const sessionCookie = response.headers
          .getSetCookie()
          .map((header) => parseSetCookie(header))
          .find((cookie) => cookie.name === DBE_SESSION_COOKIE_NAME);

        if (sessionCookie && sessionCookie.value) {
          const existingSessionId = event.context.dbeSessionId;

          if (existingSessionId !== sessionCookie.value) {
            event.context.dbeSessionId = sessionCookie.value;

            appendResponseHeader(
              event,
              "set-cookie",
              stringifySetCookie({
                name: sessionCookie.name,
                value: sessionCookie.value,
                secure: sessionCookie.secure,
                expires: sessionCookie.expires,
                httpOnly: sessionCookie.httpOnly,
                sameSite: sessionCookie.sameSite,
                path: sessionCookie.path,
              }),
            );
          }
        }
      } catch {
        // Cookie not found - next request it will attempt to set it again
      }

      return response;
    },
  });

  const hydratedRepos = {
    bootstrapRepo: {
      startWo: (params: Parameters<typeof repos.bootstrapRepo.startWo>[1]) =>
        repos.bootstrapRepo.startWo(service, params),
    },
    authRepo: {
      emailLookup: (params: Parameters<typeof repos.authRepo.emailLookup>[1]) =>
        repos.authRepo.emailLookup(service, params),
      injectJWT: (params: Parameters<typeof repos.authRepo.injectJWT>[1]) =>
        repos.authRepo.injectJWT(service, params),
      requestOtp: (params: Parameters<typeof repos.authRepo.requestOtp>[1]) =>
        repos.authRepo.requestOtp(service, params),
      setGuest: (params: Parameters<typeof repos.authRepo.setGuest>[1]) =>
        repos.authRepo.setGuest(service, params),
      signIn: (params: Parameters<typeof repos.authRepo.signIn>[1]) =>
        repos.authRepo.signIn(service, params),
      signOut: () => repos.authRepo.signOut(service),
      signUp: (params: Parameters<typeof repos.authRepo.signUp>[1]) =>
        repos.authRepo.signUp(service, params),
      exchangeSsoToken: (
        params: Parameters<typeof repos.authRepo.exchangeSsoToken>[1],
      ) => repos.authRepo.exchangeSsoToken(service, params),
    },
    discountRepo: {
      getDiscounts: (
        params: Parameters<typeof repos.discountRepo.getDiscounts>[1],
      ) => repos.discountRepo.getDiscounts(service, params),
    },
    businessRepo: {
      getFeatureMatrixToggles: (
        params: Parameters<
          typeof repos.businessRepo.getFeatureMatrixToggles
        >[1],
      ) => repos.businessRepo.getFeatureMatrixToggles(service, params),
      getMedias: (params: Parameters<typeof repos.businessRepo.getMedias>[1]) =>
        repos.businessRepo.getMedias(service, params),
      getOutlets: (
        params: Parameters<typeof repos.businessRepo.getOutlets>[1],
      ) => repos.businessRepo.getOutlets(service, params),
      getCoverageForCoordinates: (
        params: Parameters<
          typeof repos.businessRepo.getCoverageForCoordinates
        >[1],
      ) => repos.businessRepo.getCoverageForCoordinates(service, params),
    },
    basketRepo: {
      getBasket: (params: Parameters<typeof repos.basketRepo.getBasket>[1]) =>
        repos.basketRepo.getBasket(service, params),
      getBaskets: () => repos.basketRepo.getBaskets(service),
      createBasket: (
        params: Parameters<typeof repos.basketRepo.createBasket>[1],
      ) => repos.basketRepo.createBasket(service, params),
      recreateBasket: (
        params: Parameters<typeof repos.basketRepo.recreateBasket>[1],
      ) => repos.basketRepo.recreateBasket(service, params),
      getCrossSellItems: (
        params: Parameters<typeof repos.basketRepo.getCrossSellItems>[1],
      ) => repos.basketRepo.getCrossSellItems(service, params),
      addItem: (params: Parameters<typeof repos.basketRepo.addItem>[1]) =>
        repos.basketRepo.addItem(service, params),
      updateItemQuantity: (
        params: Parameters<typeof repos.basketRepo.updateItemQuantity>[1],
      ) => repos.basketRepo.updateItemQuantity(service, params),
      clearItems: (params: Parameters<typeof repos.basketRepo.clearItems>[1]) =>
        repos.basketRepo.clearItems(service, params),
      deleteBasket: (params: Parameters<typeof repos.basketRepo.destroy>[1]) =>
        repos.basketRepo.destroy(service, params),
      applyDiscount: (
        params: Parameters<typeof repos.basketRepo.applyDiscount>[1],
      ) => repos.basketRepo.applyDiscount(service, params),
      removeDiscount: (
        params: Parameters<typeof repos.basketRepo.removeDiscount>[1],
      ) => repos.basketRepo.removeDiscount(service, params),
      getLoyaltyPrograms: (
        params: Parameters<typeof repos.basketRepo.getLoyaltyPrograms>[1],
      ) => repos.basketRepo.getLoyaltyPrograms(service, params),
      applyLoyaltyProgram: (
        params: Parameters<typeof repos.basketRepo.applyLoyaltyProgram>[1],
      ) => repos.basketRepo.applyLoyaltyProgram(service, params),
      removeLoyaltyProgram: (
        params: Parameters<typeof repos.basketRepo.removeLoyaltyProgram>[1],
      ) => repos.basketRepo.removeLoyaltyProgram(service, params),
      setLoyaltyWalletCash: (
        params: Parameters<typeof repos.basketRepo.setLoyaltyWalletCash>[1],
      ) => repos.basketRepo.setLoyaltyWalletCash(service, params),
      setNote: (params: Parameters<typeof repos.basketRepo.setNote>[1]) =>
        repos.basketRepo.setNote(service, params),
      setMeta: (params: Parameters<typeof repos.basketRepo.setMeta>[1]) =>
        repos.basketRepo.setMeta(service, params),
      setDeliveryLocation: (
        params: Parameters<typeof repos.basketRepo.setDeliveryLocation>[1],
      ) => repos.basketRepo.setDeliveryLocation(service, params),
      setGiftCard: (
        params: Parameters<typeof repos.basketRepo.setGiftCard>[1],
      ) => repos.basketRepo.setGiftCard(service, params),
      setScheduledTime: (
        params: Parameters<typeof repos.basketRepo.setScheduledTime>[1],
      ) => repos.basketRepo.setScheduledTime(service, params),
      setVehicleInfo: (
        params: Parameters<typeof repos.basketRepo.setVehicleInfo>[1],
      ) => repos.basketRepo.setVehicleInfo(service, params),
      checkout: (params: Parameters<typeof repos.basketRepo.checkout>[1]) =>
        repos.basketRepo.checkout(service, params),
    },
    customerRepo: {
      profile: () => repos.customerRepo.profile(service),
      updateProfile: (
        params: Parameters<typeof repos.customerRepo.updateProfile>[1],
      ) => repos.customerRepo.updateProfile(service, params),
      deleteAccount: () => repos.customerRepo.deleteAccount(service),
      getAddresses: (
        params: Parameters<typeof repos.customerRepo.getAddresses>[1],
      ) => repos.customerRepo.getAddresses(service, params),
      getOrderHistory: (
        params: Parameters<typeof repos.customerRepo.getOrderHistory>[1],
      ) => repos.customerRepo.getOrderHistory(service, params),
      getPastOrders: (
        params: Parameters<typeof repos.customerRepo.getPastOrders>[1],
      ) => repos.customerRepo.getPastOrders(service, params),
      createAddress: (
        params: Parameters<typeof repos.customerRepo.createAddress>[1],
      ) => repos.customerRepo.createAddress(service, params),
      updateAddress: (
        params: Parameters<typeof repos.customerRepo.updateAddress>[1],
      ) => repos.customerRepo.updateAddress(service, params),
      deleteAddress: (
        params: Parameters<typeof repos.customerRepo.deleteAddress>[1],
      ) => repos.customerRepo.deleteAddress(service, params),
      getCommunicationPreferences: (
        params: Parameters<
          typeof repos.customerRepo.getCommunicationPreferences
        >[1],
      ) => repos.customerRepo.getCommunicationPreferences(service, params),
      updateCommunicationPreferences: (
        params: Parameters<
          typeof repos.customerRepo.updateCommunicationPreferences
        >[1],
      ) => repos.customerRepo.updateCommunicationPreferences(service, params),
    },
    giftCardRepo: {
      getProviders: (
        params: Parameters<typeof repos.giftCardRepo.getProviders>[1],
      ) => repos.giftCardRepo.getProviders(service, params),
      applyGiftCard: (
        params: Parameters<typeof repos.giftCardRepo.applyGiftCard>[1],
      ) => repos.giftCardRepo.applyGiftCard(service, params),
      getGiftCardBalance: (
        params: Parameters<typeof repos.giftCardRepo.getGiftCardBalance>[1],
      ) => repos.giftCardRepo.getGiftCardBalance(service, params),
    },
    loyaltyRepo: {
      getSSoProvider: (
        params: Parameters<typeof repos.loyaltyRepo.getSSoProvider>[1],
      ) => repos.loyaltyRepo.getSSoProvider(service, params),
      joinProgram: (
        params: Parameters<typeof repos.loyaltyRepo.joinProgram>[1],
      ) => repos.loyaltyRepo.joinProgram(service, params),
      getCustomerProfile: (
        params: Parameters<typeof repos.loyaltyRepo.getCustomerProfile>[1],
      ) => repos.loyaltyRepo.getCustomerProfile(service, params),
      getSettings: (
        params: Parameters<typeof repos.loyaltyRepo.getSettings>[1],
      ) => repos.loyaltyRepo.getSettings(service, params),
    },
    outletRepo: {
      getOutletById: (
        params: Parameters<typeof repos.outletRepo.getOutletById>[1],
      ) => repos.outletRepo.getOutletById(service, params),
      getOutletBySlug: (
        params: Parameters<typeof repos.outletRepo.getOutletBySlug>[1],
      ) => repos.outletRepo.getOutletBySlug(service, params),
      getPickupOutlets: (
        params: Parameters<typeof repos.outletRepo.getPickupOutlets>[1],
      ) => repos.outletRepo.getPickupOutlets(service, params),
      getSettings: (
        params: Parameters<typeof repos.outletRepo.getSettings>[1],
      ) => repos.outletRepo.getSettings(service, params),
      getPickupCities: (
        params: Parameters<typeof repos.outletRepo.getPickupCities>[1],
      ) => repos.outletRepo.getPickupCities(service, params),
      getMenus: (params: Parameters<typeof repos.outletRepo.getMenus>[1]) =>
        repos.outletRepo.getMenus(service, params),
      getMenu: (params: Parameters<typeof repos.outletRepo.getMenu>[1]) =>
        repos.outletRepo.getMenu(service, params),
      getTimeSlots: (
        params: Parameters<typeof repos.outletRepo.getTimeSlots>[1],
      ) => repos.outletRepo.getTimeSlots(service, params),
      getPaymentMethods: (
        params: Parameters<typeof repos.outletRepo.getPaymentMethods>[1],
      ) => repos.outletRepo.getPaymentMethods(service, params),
      getCoverages: (
        params: Parameters<typeof repos.outletRepo.getCoverages>[1],
      ) => repos.outletRepo.getCoverages(service, params),
    },
    orderRepo: {
      addReview: (params: Parameters<typeof repos.orderRepo.addReview>[1]) =>
        repos.orderRepo.addReview(service, params),
      getById: (params: Parameters<typeof repos.orderRepo.getById>[1]) =>
        repos.orderRepo.getById(service, params),
      getByTrackingId: (
        params: Parameters<typeof repos.orderRepo.getByTrackingId>[1],
      ) => repos.orderRepo.getByTrackingId(service, params),
      getOngoingOrders: (
        params: Parameters<typeof repos.orderRepo.getOngoingOrders>[1],
      ) => repos.orderRepo.getOngoingOrders(service, params),
      pendingReview: (
        params: Parameters<typeof repos.orderRepo.pendingReview>[1],
      ) => repos.orderRepo.pendingReview(service, params),
      skipReview: (params: Parameters<typeof repos.orderRepo.skipReview>[1]) =>
        repos.orderRepo.skipReview(service, params),
    },
    menuRepo: {
      getItem: (params: Parameters<typeof repos.menuRepo.getItem>[1]) =>
        repos.menuRepo.getItem(service, params),
      getItems: (params: Parameters<typeof repos.menuRepo.getItems>[1]) =>
        repos.menuRepo.getItems(service, params),
      getItemSchedules: (
        params: Parameters<typeof repos.menuRepo.getItemSchedules>[1],
      ) => repos.menuRepo.getItemSchedules(service, params),
    },
    storeRepo: {
      getStore: (params: Parameters<typeof repos.storeRepo.getStore>[1]) =>
        repos.storeRepo.getStore(service, params),
      getStores: (params: Parameters<typeof repos.storeRepo.getStores>[1]) =>
        repos.storeRepo.getStores(service, params),
    },
  };

  return hydratedRepos;
};
